import { TFunction } from 'i18next';
import { HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest } from '../api/HealthSystemApi';
import { AnimalGender, AnimalType } from '../models/animal';
import { geneticFailureLabel, observationCategoryLabel } from './animalObservations';

/** Date field the from/to range is matched against. Values are the backend's `filterDateBy` strings. */
export const DEFAULT_FILTER_DATE_BY = 'DateOfBirth';

const FILTER_DATE_BY_LABEL_KEYS: Record<string, string> = {
  DateOfBirth: 'f-filter-date-by-date-of-birth',
  RegisteredAt: 'f-filter-date-by-registered-at',
  DeathDate: 'f-filter-date-by-death-date',
  SlaughteredDate: 'f-filter-date-by-slaughtered-date'
};

/**
 * Translation key naming the date field a search filters on. Shared by the active-filter
 * chips and the saved-query summary so a new option only has to be added in one place.
 */
export const filterDateByLabelKey = (filterDateBy?: string | null): string =>
  FILTER_DATE_BY_LABEL_KEYS[filterDateBy || DEFAULT_FILTER_DATE_BY] ??
  FILTER_DATE_BY_LABEL_KEYS[DEFAULT_FILTER_DATE_BY];

/**
 * How a sow or boar column selection reads. Shared by the active-filter chip and the saved-query
 * summary, so the numbers and the blanks entry are spelled out the same way in both.
 */
export const describeParentNumbersFilter = (
  numbers: string[] | null | undefined,
  includeWithoutParent: boolean | null | undefined,
  blankLabel: string
): string => {
  const parts = [...(numbers ?? [])];

  if (includeWithoutParent) {
    parts.push(blankLabel);
  }

  return parts.join(', ');
};

/**
 * Human-readable summary of the filters baked into a saved search query.
 * Mirrors the same "isXSearch" flags and translation keys as the active-filter
 * chips on the animal search page (src/pages/AnimalSearch/component.tsx), so a
 * saved search reads the same way here as it did when it was created.
 */
export const summarizeSearchQueryFilters = (
  query: HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest,
  t: TFunction,
  formatDate: (dateStr: string) => string
): string[] => {
  // The saved query JSON can carry extra display-name fields (disease/mortalityReason/medicalSupply)
  // that aren't part of the strict request type - see buildSearchQuery in AnimalSearch/component.tsx.
  const extra = query as HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest & {
    disease?: { name?: string } | null;
    mortalityReason?: { name?: string } | null;
    medicalSupply?: { name?: string } | null;
  };

  const filters: string[] = [];

  if (query.number) {
    filters.push(`${t('f-filter-chip-number')}: ${query.number}`);
  }
  if (query.findByExternalIds) {
    filters.push(t('f-filter-external-ids'));
  }
  if (query.genetics) {
    filters.push(`${t('f-filter-genetics')}: ${query.genetics}`);
  }
  if (query.motherNumbers?.length || query.includeWithoutMother) {
    filters.push(
      `${t('f-search-sow-number')}: ${describeParentNumbersFilter(
        query.motherNumbers,
        query.includeWithoutMother,
        t('f-column-filter-no-sow')
      )}`
    );
  }
  if (query.fatherNumbers?.length || query.includeWithoutFather) {
    filters.push(
      `${t('f-search-boar-number')}: ${describeParentNumbersFilter(
        query.fatherNumbers,
        query.includeWithoutFather,
        t('f-column-filter-no-boar')
      )}`
    );
  }
  if (query.animalType) {
    filters.push(`${t('f-filter-animal-type')}: ${t(AnimalType[query.animalType])}`);
  }
  if (query.gender) {
    filters.push(query.gender === AnimalGender.Male ? t('f-filter-gender-male') : t('f-filter-gender-female'));
  }
  if (query.dateFrom || query.dateTo) {
    const from = query.dateFrom ? formatDate(query.dateFrom) : '…';
    const to = query.dateTo ? formatDate(query.dateTo) : '…';
    filters.push(`${t(filterDateByLabelKey(query.filterDateBy))}: ${from} - ${to}`);
  }
  if (query.weightPerDay?.[0]) {
    filters.push(`${t('f-filter-weight-per-day-from')}: ${query.weightPerDay[0]}`);
  }
  if (query.weightPerDay?.[1]) {
    filters.push(`${t('f-filter-weight-per-day-to')}: ${query.weightPerDay[1]}`);
  }
  if (query.locationId) {
    filters.push(t('f-filter-location'));
  }
  if (query.isWeanedDaysSearch) {
    filters.push(t('f-filter-weaned-days'));
  }
  if (query.isAntibioticFreeSearch) {
    filters.push(t('f-filter-antibiotic-free'));
  }
  if (query.isSquareMetersPerPigSearch) {
    filters.push(t('f-filter-sq-meters-per-pig'));
  }
  if (query.isWeightGroupSearch) {
    filters.push(t('f-filter-weight-group'));
  }
  if (query.isWeightCategorySearch) {
    filters.push(t('f-filter-weight-category'));
  }
  if (query.isAnimalObservationSearch) {
    const picked = [
      ...(query.observationCategories ?? []).map((it) => observationCategoryLabel(t, it)),
      ...(query.observationGeneticFailures ?? []).map((it) => geneticFailureLabel(t, it))
    ];
    filters.push(
      picked.length > 0 ? `${t('f-filter-animal-observation')}: ${picked.join(', ')}` : t('f-filter-animal-observation')
    );
  }
  if (query.isInjectionSearch) {
    const supplyName = extra.medicalSupply?.name;
    filters.push(supplyName ? `${t('f-filter-injection')}: ${supplyName}` : t('f-filter-injection'));
  }
  if (query.isDeathSearch) {
    const diseaseName = extra.disease?.name;
    filters.push(diseaseName ? `${t('f-filter-death-disease')}: ${diseaseName}` : t('f-filter-death-disease'));
  }
  if (query.isMortalityReasonSearch) {
    const reasonName = extra.mortalityReason?.name;
    filters.push(reasonName ? `${t('f-filter-mortality-reason')}: ${reasonName}` : t('f-filter-mortality-reason'));
  }
  if (query.isSlaughterhouseSearch) {
    filters.push(t('f-filter-slaughterhouse'));
  }
  if (query.isClimateControlSearch) {
    filters.push(t('f-filter-climate-control'));
  }
  if (query.isBirthCycleSearch) {
    filters.push(t('f-filter-birth-cycle'));
  }
  if (query.configurationProviderType) {
    filters.push(`${t('f-filter-provider-type')}: ${t('f-filter-provider-pigexpert')}`);
  }
  if (query.excludeImported) {
    filters.push(t('f-filter-exclude-imported'));
  }

  return filters;
};
